import React, { useEffect } from "react";
import { Box, Divider, useColorScheme } from "@mui/joy";
import { Plus } from "lucide-react";

import { InspectionTaskComponent } from "../../../../components/Inspection/components";
import { InspectionV2 } from "../../../";
import { TaskComponent as EstimateContractTaskComponent } from "../../../../components/EstimateContract/components";
import { ContractTaskComponent } from "../../../../components/ContractSigning/components";
import { MaterialOrderingTaskComponent } from "../../../../components/MaterialOrdering/components";
import { JobExecutionTaskComponent } from "../../../../components/JobExecution/components";
import { FinalSettlementTaskComponent } from "../../../../components/FinalSettlement/components";

import { Button, ProgressStepper } from "../../../../shared";
import { useResponsive } from "../../../../hooks";

import { useTaskHandler } from "./hooks";
import { BuildingPermitTaskComponent } from "../../../BuildingPermit/component";

import { Skeleton, Stack } from "@mui/joy";
import { Alert } from "../../../../shared";

export default function ListTask() {
    const {
        currentStep,
        taskList,
        handleClickOnProgressBar,
        activeTask,
        measurements,
        setMeasurements,
        attributes,
        setAttributes,
        actionProgress,
        handleOnEstimateCompletion,
        handleOnInspectionCompletion,
        handleOnContractSigningCompletion,
        handleJobExecutionCompletion,
        handleFinalSettlementTaskCompletion,
        handleProjectStatusUpdate,
        listTasksByProjectSvc,
    } = useTaskHandler();

    const { mode } = useColorScheme();

    const isLoading = listTasksByProjectSvc.isPending && taskList.length === 0;

    const { isMobile } = useResponsive();

    useEffect(() => {
        const handleSwitch = () => {
            window.localStorage.setItem("use-inspection-v2", "true");
            window.location.reload();
        };
        window.addEventListener("switch-to-v2", handleSwitch);
        return () => window.removeEventListener("switch-to-v2", handleSwitch);
    }, []);

    return (
        <Box mt={-2} mx={0}>
            <Box
                sx={{
                    position: "sticky",
                    top: "48px",
                    width: "100%",
                    zIndex: 1002,
                    bgcolor: mode === "light" ? "white" : "#1e1e1e",
                    pt: 2,
                }}
            >
                <Box mb={2} mt={1} display={"flex"} alignItems={"center"} justifyContent={"space-between"} gap={2}>
                    {isLoading ? (
                        <Skeleton variant="rectangular" width="80%" height={37} sx={{ borderRadius: "30px" }} />
                    ) : (
                        <Box sx={{ flex: 1, minWidth: 0, overflow: "auto" }}>
                            <ProgressStepper
                                currentStep={currentStep}
                                steps={taskList}
                                onClick={handleClickOnProgressBar}
                            />
                        </Box>
                    )}
                    <Divider orientation="vertical" />
                    {isLoading ? (
                        <Skeleton variant="rectangular" width={100} height={37} sx={{ borderRadius: "md" }} />
                    ) : isMobile ? null : (
                        <Button
                            variant={"outlined"}
                            color={"neutral"}
                            startDecorator={<Plus size={14} />}
                            sx={{ flexShrink: 0 }}
                        >
                            Add task
                        </Button>
                    )}
                </Box>
                <Divider inset="context" sx={{ ml: -2, mr: -2 }} />
            </Box>

            {listTasksByProjectSvc.isError && (
                <Box mt={2}>
                    <Alert
                        errorMessage={(listTasksByProjectSvc.error as any)?.message || "Failed to load project tasks"}
                    />
                </Box>
            )}

            <Box display={"flex"} gap={2} flexDirection={"column"} mt={2}>
                {isLoading ? (
                    <Stack spacing={3} p={2}>
                        <Box display="flex" justifyContent="space-between">
                            <Skeleton variant="text" width="30%" height={32} />
                            <Skeleton variant="rectangular" width={120} height={36} sx={{ borderRadius: "md" }} />
                        </Box>
                        <Stack spacing={2}>
                            {[...Array(5)].map((_, i) => (
                                <Box key={i} sx={{ display: "flex", gap: 2, alignItems: "center", py: 1 }}>
                                    <Skeleton variant="circular" width={24} height={24} />
                                    <Skeleton variant="text" width="70%" />
                                    <Skeleton
                                        variant="rectangular"
                                        width={60}
                                        height={20}
                                        sx={{ borderRadius: "xl" }}
                                    />
                                </Box>
                            ))}
                        </Stack>
                    </Stack>
                ) : (
                    <>
                        {activeTask?.Name === "Initial Inspection" && (
                            <>
                                {window.localStorage.getItem("use-inspection-v2") === "true" ? (
                                    <Box>
                                        <Button
                                            size="sm"
                                            variant="outlined"
                                            color="neutral"
                                            onClick={() => {
                                                window.localStorage.removeItem("use-inspection-v2");
                                                window.location.reload();
                                            }}
                                            sx={{ mb: 2 }}
                                        >
                                            Back to Classic View
                                        </Button>
                                        <InspectionV2
                                            activeTask={activeTask}
                                            attributes={attributes}
                                            setAttributes={setAttributes}
                                            measurements={measurements}
                                            setMeasurements={setMeasurements}
                                            onComplete={handleOnInspectionCompletion}
                                        />
                                    </Box>
                                ) : (
                                    <InspectionTaskComponent
                                        activeTask={activeTask}
                                        measurements={measurements}
                                        setMeasurements={setMeasurements}
                                        attributes={attributes}
                                        setAttributes={setAttributes}
                                        onComplete={handleOnInspectionCompletion}
                                    />
                                )}
                            </>
                        )}

                        {activeTask?.Name === "Estimate/Contract" && (
                            <EstimateContractTaskComponent
                                activeTask={activeTask}
                                onComplete={handleOnEstimateCompletion}
                                actionProgress={actionProgress}
                                measurements={measurements}
                                setMeasurements={setMeasurements}
                                attributes={attributes}
                                setAttributes={setAttributes}
                            />
                        )}
                        {activeTask?.Name === "Contract Signing" && (
                            <ContractTaskComponent
                                activeTask={activeTask}
                                measurements={measurements}
                                onComplete={handleOnContractSigningCompletion}
                                actionProgress={actionProgress}
                            />
                        )}
                        {activeTask?.Name === "Material Ordering" && (
                            <MaterialOrderingTaskComponent
                                activeTask={activeTask}
                                onComplete={handleOnInspectionCompletion}
                                actionProgress={actionProgress}
                            />
                        )}
                        {activeTask?.Name === "Building Permit" && (
                            <BuildingPermitTaskComponent
                                activeTask={activeTask}
                                onComplete={handleOnInspectionCompletion}
                                actionProgress={actionProgress}
                            />
                        )}
                        {activeTask?.Name === "Job Execution" && (
                            <JobExecutionTaskComponent
                                activeTask={activeTask}
                                onComplete={handleJobExecutionCompletion}
                                actionProgress={actionProgress}
                            />
                        )}
                        {activeTask?.Name === "Final Settlement" && (
                            <FinalSettlementTaskComponent
                                activeTask={activeTask}
                                onTaskClose={handleFinalSettlementTaskCompletion}
                                onProjectClose={handleProjectStatusUpdate}
                                actionProgress={actionProgress}
                            />
                        )}
                    </>
                )}
            </Box>
        </Box>
    );
}
